import isAuthenticated from '@/router/guards/isAuthenticated';
import isGuest from '@/router/guards/isGuest';
import isOnboarding from '@/router/guards/isOnboarding';
import { useStore } from '@/stores/main';
import { createRouter, createWebHistory } from '@ionic/vue-router';
import { RouteRecordRaw } from 'vue-router';

// Questionnaire, results and AI code load when their route is opened.
const AiChatPage = () => import('@/views/AiChatPage.vue');
const GoalsImprovePage = () => import('@/views/GoalsImprovePage.vue');
const GoalsPage = () => import('@/views/GoalsPage.vue');
const MyGoalsPage = () => import('@/views/MyGoalsPage.vue');
const HomePage = () => import('@/views/HomePage.vue');
const LoginPage = () => import('@/views/LoginPage.vue');
const RegisterPage = () => import('@/views/RegisterPage.vue');
const VerifyEmailPage = () => import('@/views/VerifyEmailPage.vue');
const CompleteRegistrationPage = () => import('@/views/CompleteRegistrationPage.vue');
const AccountHandoffPage = () => import('@/views/AccountHandoffPage.vue');
const MinLivsstilPage = () => import('@/views/MinLivsstilPage.vue');
const MittArbetePage = () => import('@/views/MittArbetePage.vue');
const OverviewPage = () => import('@/views/OverviewPage.vue');
const PlanAiIntroPage = () => import('@/views/PlanAiIntroPage.vue');
const PlanPage = () => import('@/views/PlanPage.vue');
const PlanPreludePage = () => import('@/views/PlanPreludePage.vue');
const PlanActionPage = () => import('@/views/PlanActionPage.vue');
const PlanWritePage = () => import('@/views/PlanWritePage.vue');
const StartPage = () => import('@/views/StartPage.vue');

const skipPlanPrelude = async (to: any) => {
  const authResult = await isAuthenticated(to);
  if (authResult !== true) return authResult;

  const store = useStore();
  try {
    const goals = await store.fetchPlannedGoals();
    const firstGoalId = goals?.[0]?.category_id;
    if (firstGoalId) {
      return { path: `/plan/${firstGoalId}/actions`, replace: true };
    }
  } catch {
    // Fall back to the old plan page if goals cannot be loaded.
  }

  return { path: '/plan', replace: true };
};

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    name: 'Start',
    component: StartPage,
    beforeEnter: isGuest,
  },
  {
    path: '/login',
    name: 'Login',
    component: LoginPage,
    beforeEnter: isGuest,
  },
  {
    path: '/register',
    name: 'Register',
    component: RegisterPage,
    beforeEnter: isGuest,
  },
  {
    path: '/verify-email',
    name: 'VerifyEmail',
    component: VerifyEmailPage,
    beforeEnter: isGuest,
  },
  {
    path: '/auth/handoff',
    name: 'AccountHandoff',
    component: AccountHandoffPage,
  },
  {
    path: '/complete-registration',
    name: 'CompleteRegistration',
    component: CompleteRegistrationPage,
    beforeEnter: isOnboarding,
  },
  {
    path: '/home',
    name: 'Home',
    component: OverviewPage,
    beforeEnter: isAuthenticated,
  },
  {
    path: '/evaluate',
    name: 'Evaluate',
    component: HomePage,
    beforeEnter: isAuthenticated,
  },
  {
    path: '/questions/1',
    name: 'MittArbete',
    component: MittArbetePage,
    props: { page: 1 },
    beforeEnter: isAuthenticated,
  },
  {
    path: '/questions/2',
    name: 'MinLivsstil',
    component: MinLivsstilPage,
    props: { page: 2 },
    beforeEnter: isAuthenticated,
  },
  {
    path: '/goals/happy',
    name: 'Goals',
    component: GoalsPage,
    beforeEnter: isAuthenticated,
  },
  {
    path: '/goals/reflect',
    name: 'GoalsMidPage',
    redirect: '/goals/improve',
  },
  {
    path: '/goals/improve',
    name: 'GoalsImprovePage',
    component: GoalsImprovePage,
    beforeEnter: isAuthenticated,
  },
  {
    path: '/goals/improve/complete/:sectionKey/factors',
    name: 'GoalsImproveCategoryFactorsPage',
    component: GoalsImprovePage,
    beforeEnter: isAuthenticated,
  },
  {
    path: '/goals/improve/complete/:sectionKey',
    name: 'GoalsImproveCategoryPage',
    redirect: '/goals/improve',
  },
  {
    path: '/goals/reminder',
    name: 'GoalsPhysicalTestsReminder',
    redirect: '/goals/happy',
    beforeEnter: isAuthenticated,
  },
  {
    path: '/goals',
    name: 'GoalsStartPage',
    redirect: '/goals/happy',
    beforeEnter: isAuthenticated,
  },
  {
    path: '/plan/prelude',
    name: 'PlanPrelude',
    component: PlanPreludePage,
    beforeEnter: skipPlanPrelude,
  },
  {
    path: '/plan',
    name: 'Plan',
    component: PlanPage,
    beforeEnter: isAuthenticated,
  },
  {
    path: '/plan/:goalId/actions',
    name: 'PlanAction',
    component: PlanActionPage,
    beforeEnter: isAuthenticated,
  },
  {
    path: '/plan/:goalId/write',
    name: 'PlanWrite',
    component: PlanWritePage,
    beforeEnter: isAuthenticated,
  },
  {
    path: '/plan/ai-intro/:goalCategoryId',
    name: 'PlanAiIntro',
    component: PlanAiIntroPage,
    beforeEnter: isAuthenticated,
  },
  {
    path: '/plan/results',
    name: 'PlanResults',
    redirect: '/my-goals',
    beforeEnter: isAuthenticated,
  },
  {
    path: '/my-goals',
    name: 'MyGoals',
    component: MyGoalsPage,
    beforeEnter: isAuthenticated,
  },
  {
    path: '/my-tasks',
    name: 'MyTasks',
    redirect: (to) => ({ path: '/my-goals', query: to.query }),
    beforeEnter: isAuthenticated,
  },
  {
    path: '/ai-chat/:goalCategoryId',
    name: 'AiChat',
    component: AiChatPage,
    beforeEnter: isAuthenticated,
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

export default router;
